import React, { useMemo } from 'react';
import { View } from 'react-native';
import { useAnimatedProps, useDerivedValue } from 'react-native-reanimated';
import BlurAmountView from 'design-system/BlurAmountView';
import useStyles from 'hooks/useStyles';
import { rem } from 'design-system/values';
import createStyleSheets from 'utils/createStyleSheets';
import Text from 'design-system/Text';
import { Spacer } from 'design-system';
import { AnimatedTextInput } from 'design-system/AnimatedComponents';
import useColors from 'hooks/useColors';
import { useAppSelector } from 'store/hooks';
import { formatAmount } from '../utils';
import formatAmountWorklet from '../worklets/formatAmount';
import useFormatTimestampWorklet from '../hooks/useFormatTimestampWorklet';
import { selectIsTradingAccountAlpaca } from '../reducers/selectors';
const PriceChangeTimeSeccl = ({ percent, amount, contextValue, period, scrambled }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const isAlpacaAccount = useAppSelector(selectIsTradingAccountAlpaca);
    const { originalDataShared, providedData, originalX } = contextValue || {};
    const formatTimestampWorklet = useFormatTimestampWorklet(originalDataShared, 'portfolio', false, period, isAlpacaAccount);
    const selectedValue = useDerivedValue(() => {
        if (originalDataShared?.value) {
            if (originalX?.value) {
                if (!providedData || providedData.points.length === 0)
                    return undefined;
                const x = parseInt(originalX.value, 10);
                const data = originalDataShared.value;
                return {
                    timestamp: data.timestamp[x],
                    profitLoss: data.profitLoss[x],
                    profitLossPct: data.profitLossPct[x],
                };
            }
        }
        return undefined;
    }, [providedData, originalDataShared, originalX]);
    const timeProps = useAnimatedProps(() => ({
        text: formatTimestampWorklet(selectedValue.value?.timestamp || ''),
    }), [formatTimestampWorklet, selectedValue]);
    const defaultValues = useMemo(() => {
        let profitSign = '';
        let profitPctSign = '';
        let percentColor = colors.text.primary;
        let amountColor = colors.text.primary;
        if (percent > 0) {
            profitPctSign = '+';
            percentColor = colors.text.statusPositive;
        }
        if (percent < 0) {
            profitPctSign = '-';
            percentColor = colors.text.statusNegative;
        }
        if (amount.value > 0) {
            profitSign = '+';
            amountColor = colors.text.statusPositive;
        }
        if (amount.value < 0) {
            profitSign = '-';
            amountColor = colors.text.statusNegative;
        }
        return {
            amountColor,
            percentColor,
            profitPct: `${profitPctSign}${Math.abs(percent).toFixed(2)}%`,
            profit: profitSign +
                formatAmount({
                    value: Math.abs(amount.value),
                    currency: amount.currency,
                }),
        };
    }, [
        amount.currency,
        amount.value,
        colors.text.primary,
        colors.text.statusNegative,
        colors.text.statusPositive,
        percent,
    ]);
    const profitPctProps = useAnimatedProps(() => {
        if (selectedValue.value?.profitLossPct !== undefined && selectedValue.value?.profitLossPct !== null) {
            const value = selectedValue.value?.profitLossPct;
            let sign = '';
            let color = colors.text.primary;
            if (value > 0) {
                sign = '+';
                color = colors.text.statusPositive;
            }
            if (value < 0) {
                sign = '-';
                color = colors.text.statusNegative;
            }
            return {
                color,
                text: `${sign}${Math.abs(value).toFixed(2)}%`,
            };
        }
        return {
            color: defaultValues.percentColor,
            text: defaultValues.profitPct,
        };
    }, [
        colors.text.primary,
        colors.text.statusNegative,
        colors.text.statusPositive,
        defaultValues.percentColor,
        defaultValues.profitPct,
        selectedValue,
    ]);
    const profitProps = useAnimatedProps(() => {
        if (selectedValue.value?.profitLoss !== undefined && selectedValue.value?.profitLoss !== null) {
            const value = selectedValue.value?.profitLoss;
            let sign = '';
            let color = colors.text.primary;
            if (value > 0) {
                sign = '+';
                color = colors.text.statusPositive;
            }
            if (value < 0) {
                sign = '-';
                color = colors.text.statusNegative;
            }
            return {
                color,
                text: `${sign}${formatAmountWorklet(Math.abs(Number(value)), '£')}`,
            };
        }
        return {
            color: defaultValues.amountColor,
            text: defaultValues.profit,
        };
    }, [
        colors.text.primary,
        colors.text.statusNegative,
        colors.text.statusPositive,
        defaultValues.amountColor,
        defaultValues.profit,
        selectedValue,
    ]);
    return (<View>
      <AnimatedTextInput style={styles.time} editable={false} animatedProps={timeProps} allowFontScaling={false} defaultValue={formatTimestampWorklet('')}/>
      <Spacer h={12}/>
      <View style={styles.row}>
        <View style={styles.totalReturn}>
          <BlurAmountView enabled={scrambled}>
            <AnimatedTextInput editable={false} allowFontScaling={false} animatedProps={profitProps} defaultValue={defaultValues.profit} style={[styles.return, { color: defaultValues.amountColor }]}/>
          </BlurAmountView>
          <Text TextThin-14 Secondary>
            Total return
          </Text>
        </View>
        <Spacer w={16}/>
        <View style={styles.rateOfReturn}>
          <BlurAmountView enabled={scrambled}>
            <AnimatedTextInput editable={false} allowFontScaling={false} animatedProps={profitPctProps} defaultValue={defaultValues.profitPct} style={[styles.return, { color: defaultValues.percentColor }]}/>
          </BlurAmountView>
          <Text TextThin-14 Secondary>
            Rate of return
          </Text>
        </View>
      </View>
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    row: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    time: {
        padding: 0,
        fontSize: rem(14),
        fontWeight: '300',
        fontFamily: 'Montserrat',
        color: colors.text.secondary,
    },
    totalReturn: {
        minWidth: '35%',
    },
    rateOfReturn: {
        flex: 1,
    },
    return: {
        padding: 0,
        fontWeight: '700',
        fontSize: rem(16),
        fontFamily: 'Nunito',
        color: colors.text.primary,
    },
}));
export default PriceChangeTimeSeccl;
